import React, { useEffect, useState } from 'react';
import _classNames from 'classnames';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useFirestore } from 'react-redux-firebase';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import ButtonBase from '@material-ui/core/ButtonBase';
import InputBase from '@material-ui/core/InputBase';
import Typography from '@material-ui/core/Typography';
import { getAssessmentResultFail } from 'containers/Assessment/actions';
import {
  makeSelectAnswers,
  makeSelectAnswersError,
} from 'containers/Assessment/selectors';
import useSiteCopySelector from 'components/useSiteCopySelector';

const useStyles = makeStyles(theme => ({
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      display: 'block',
    },
  },
  codeWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: 300,
    marginLeft: 12,
    marginTop: 0,
    marginRight: 12,
    padding: 12,
    height: 53,
    borderRadius: 5,
    background: '#FFFFFF',
    border: '1px solid #E3E3E3',
    [theme.breakpoints.down('xs')]: {
      marginTop: 10,
      marginLeft: 0,
    },
  },
  error: {
    border: '1px solid red',
  },
  input: {
    height: '100%',
    width: 200,
    ...theme.typography.body2,
  },
  button: {
    ...theme.typography.overline,
    color: theme.palette.primary.main,
    padding: 8,
  },
}));

function AccessCodeButton() {
  const { code } = useParams();
  const answers = useSelector(makeSelectAnswers());
  const answersError = useSelector(makeSelectAnswersError());
  const [inputMode, setInputMode] = useState(!code);
  const [error, setError] = useState(false);
  const [accessCode, setAccessCode] = useState(code);
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const [siteCopy] = useSiteCopySelector(['assessment-clinical-view']);
  const firestore = useFirestore();

  useEffect(() => {
    if (!_isEmpty(answers) && !_get(answers, 'accessCode')) {
      const unsubscribe = firestore
        .collection('assessment_reports')
        .doc(answers.report)
        .onSnapshot(snapshot => {
          const data = snapshot.data();
          if (data.accessCode) {
            setAccessCode(data.accessCode);
            unsubscribe();
          }
        });
    }
  }, [answers, firestore]);

  useEffect(() => {
    if (answersError) {
      setError(true);
      dispatch(getAssessmentResultFail());
    }
  }, [answersError]);

  const errorMessage = _get(siteCopy, 'pageCopy.errorMessage', '');

  const handleChangeClick = () => {
    setInputMode(true);
    setAccessCode('');
  };
  const handleChange = e => {
    setError(false);
    setAccessCode(e.target.value);
  };
  const handleSaveClick = () => {
    if (!accessCode || accessCode.length < 6) {
      setError(true);
    } else {
      history.push(`/assessments/clinicalresults/${accessCode}`);
    }
  };

  return (
    <Box component="div" className={classes.wrapper}>
      <Typography color="textPrimary" variant="h6" className={classes.label}>
        {_get(siteCopy, 'pageCopy.accessCodeLabel', 'Access Code:')}
      </Typography>
      <Box
        component="div"
        className={_classNames({
          [classes.codeWrapper]: true,
          [classes.error]: error,
        })}
      >
        {inputMode ? (
          <>
            <InputBase
              value={accessCode}
              onChange={handleChange}
              className={classes.input}
              placeholder="e.g. 456XYZ"
              inputProps={{ maxLength: 6 }}
              autoComplete="off"
            />
            <ButtonBase
              disableRipple
              onClick={handleSaveClick}
              className={classes.button}
            >
              {_get(siteCopy, 'pageCopy.saveLabel')}
            </ButtonBase>
          </>
        ) : (
          <>
            <Typography color="textPrimary" variant="body2" component="p">
              {accessCode}
            </Typography>
            <ButtonBase
              disableRipple
              onClick={handleChangeClick}
              className={classes.button}
            >
              {_get(siteCopy, 'pageCopy.changeLabel')}
            </ButtonBase>
          </>
        )}
      </Box>
      {error && errorMessage && (
        <Typography color="error" variant="overline">
          {errorMessage}
        </Typography>
      )}
    </Box>
  );
}

export default AccessCodeButton;
